import { Component, Input } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { AccountService } from '../../../../services/account.service';
import { WatchAccountService } from '../../../../services/watch-account.service';
import {
  ImportCandidatesFormInputData,
  ImportCandidatesFormOutputData,
  MappingColumn,
} from '../import-candidates-form/import-candidates-form.component';
import { parse, ParseResult } from 'papaparse';
import { unzip } from 'lodash';

const MAX_FILE_SIZE = Math.pow(10, 6); // 1 MB as bytes

@Component({
  selector: 'import-candidates-csv',
  template: require('./import-candidates-csv.component.pug'),
  styles: [require('./import-candidates-csv.component.css').toString()],
})
export class ImportCandidatesCsvComponent {
  @Input()
  modalRef: BsModalRef;

  constructor(
    private accountService: AccountService,
    private watch_account: WatchAccountService,
  ) {}

  stage: 'importForm' | 'successMessage' = 'importForm';
  isFileLoading = false;
  fileLoadingError?: string;

  isFormSubmitting = false;
  formSubmitError?: string;
  showSuccessMessage = false;

  file: File;
  presignedPost: { name: string; value: string }[];
  formInputData: ImportCandidatesFormInputData;

  dropped(files: NgxFileDropEntry[]) {
    this.fileLoadingError = undefined;
    this.formSubmitError = undefined;

    const { fileEntry } = files[0];
    if (!fileEntry.isFile) {
      return;
    }

    this.isFileLoading = true;
    (fileEntry as FileSystemFileEntry).file(file =>
      this.handleFileUpload(file),
    );
  }

  private async handleFileUpload(file: File) {
    this.file = file;
    try {
      if (this.file.size > MAX_FILE_SIZE) {
        throw new Error('Maximum file size is 1 MB');
      }

      const { presignedPost, url } = await this.accountService
        .candidateImportS3PresignedPostCreate(
          this.watch_account.accountId().getValue(),
        )
        .toPromise();

      this.presignedPost = presignedPost;
      const uploadForm = new FormData();
      this.presignedPost.forEach(({ name, value }) =>
        uploadForm.append(name, value),
      );
      uploadForm.append('file', this.file);

      await fetch(url, {
        method: 'POST',
        mode: 'no-cors',
        body: uploadForm,
      });

      parse(this.file, {
        complete: results => {
          this.formInputData = prepareFormInputData(results);
        },
      });

      this.isFileLoading = false;
    } catch (error) {
      this.fileLoadingError = error.message || 'Upload error';
      this.isFileLoading = false;
    }
  }

  onImportFormSubmit({
    mapping,
    group,
    funnel,
    updateCandidates,
  }: ImportCandidatesFormOutputData) {
    this.isFormSubmitting = true;
    this.formSubmitError = undefined;

    this.accountService
      .importCandidatesFromCsv(
        {
          accountId: this.watch_account.accountId().getValue(),
          objectKey: this.presignedPost.find(({ name }) => name === 'key')
            .value,
          mapping: mapping,
          groupId: group && group.id,
          funnelId: funnel && funnel.id,
          updateCandidates: updateCandidates,
        },
        error => {
          this.isFormSubmitting = false;
          this.formSubmitError = error;
          return null;
        },
      )
      .subscribe(
        () => {
          if (!this.formSubmitError) {
            this.isFormSubmitting = false;
            this.stage = 'successMessage';
          }
        },
        error => {
          this.isFormSubmitting = false;
          this.formSubmitError = error;
        },
      );
  }
}

function prepareFormInputData({ data }: ParseResult<any>) {
  const [headings, ...rows] = data as string[][];
  // use unzip to transpose csv rows
  const csvColumns = unzip(rows);
  const columns: MappingColumn[] = headings
    .filter(function (heading, index) {
      return csvColumns[index]; // Can have a header and yet no csvColumns for it (undefined)
    })
    .map((heading, index) => ({
      name: heading,
      preview: `${csvColumns[index].slice(0, 4).join(', ')}...`,
    }));
  return {
    columns,
    total: rows.length,
  };
}
